import useAxios, { mutate } from 'hooks/useAxios'
import React, { useState, useEffect, useRef } from 'react'
import { Message } from 'semantic-ui-react'
import { BillingDetailForm } from '.'
import { toast } from 'libs/toast'
import useFeatureToggle from 'hooks/useFeatureToggle'
import { getTaxIdErrorMessage, getTaxIdMetaForCountry } from 'libs/tax-id-by-country'
import UnsavedChangesGuard from 'shared/components/UnsavedChangesGuard'
import { getMissingUsAddressErrors, isUsBilling } from 'libs/billing/stripe-account'

// Canonical snapshot of the billing-address fields used for dirty comparison.
// Both the GET payload and the Stripe AddressElement onChange value reduce to
// the same shape, so a hydration event that merely re-emits the saved values
// does not read as a user edit.
function billingAddressSnapshot(source) {
  const address = source?.address || {}
  return JSON.stringify({
    name: source?.name || '',
    line1: address.line1 || '',
    line2: address.line2 || '',
    city: address.city || '',
    state: address.state || '',
    postal_code: address.postal_code || '',
    country: address.country || '',
  })
}

export default function BillingDetailFormContainer({ companyName, billing }) {
  const [customer, setCustomer] = useState({
    name: null,
    address: null,
  })
  const [taxIdValue, setTaxIdValue] = useState('')
  const [taxIdInlineError, setTaxIdInlineError] = useState(null)
  const [addressInlineError, setAddressInlineError] = useState(null)

  // Dirty tracking is split so an address re-emit can't clear a tax-id edit
  // (and vice versa). The unsaved-changes guard fires when EITHER is dirty.
  const [addressDirty, setAddressDirty] = useState(false)
  const [taxIdDirty, setTaxIdDirty] = useState(false)
  const isDirty = addressDirty || taxIdDirty

  // Baseline snapshot of the saved billing address, captured once the GET
  // resolves (and re-captured after an address save). The Stripe AddressElement
  // fires onChange during mount hydration — re-emitting the saved values, and
  // for a non-US saved address it can emit an intermediate default-country (US)
  // event before applying defaultValues. Comparing each change against this
  // baseline settles to "not dirty" no matter how many hydration events fire,
  // instead of the old "skip the first onChange" heuristic that flagged the
  // page dirty on load (Finding 1 / EC7).
  const addressBaselineRef = useRef(null)

  // True when the SAVED billing address has no country (empty / never set).
  // In that case Stripe's AddressElement auto-selects a default country on
  // mount and emits an onChange for it — pure hydration, not a user edit. The
  // baseline snapshot of an empty address has country '' , so that auto-country
  // emit would otherwise read as dirty and fire the unsaved-changes guard on an
  // untouched Billing page (prod bug: guard triggered with zero interaction for
  // customers with no saved address). handleCustomerChange adopts that first
  // emit into the baseline instead of flagging it dirty.
  const savedAddressWasEmptyRef = useRef(false)

  // Resets dirty state after a successful save so navigation away no longer
  // warns (EC8). `rebaseline` re-captures the address baseline from the just-
  // saved customer — used by the address-save paths, skipped by the tax-id-only
  // path (which never touched the address).
  function markSaved({ rebaseline = false } = {}) {
    setAddressDirty(false)
    setTaxIdDirty(false)
    if (rebaseline) {
      addressBaselineRef.current = billingAddressSnapshot(customer)
    }
  }

  // Wraps setCustomer so any address interaction clears a stale inline
  // "Please complete your billing address before saving." error — the
  // signal that the user is fixing the address they were blocked on.
  function handleCustomerChange(customerData) {
    setCustomer(customerData)
    setAddressInlineError(null)
    // No saved country → the first AddressElement emit is Stripe's mount-time
    // auto-country selection, not a user edit. Adopt it as the baseline (once)
    // so an untouched page with no saved address never reads as dirty. Genuine
    // later edits still compare against this baseline and flip dirty.
    if (savedAddressWasEmptyRef.current) {
      savedAddressWasEmptyRef.current = false
      addressBaselineRef.current = billingAddressSnapshot(customerData)
      setAddressDirty(false)
      return
    }
    // Dirty only when the value genuinely differs from the saved baseline —
    // immune to mount-time hydration re-emits. Before the baseline is captured
    // (null), stay clean so a race can't false-flag on load.
    setAddressDirty(
      addressBaselineRef.current !== null &&
        billingAddressSnapshot(customerData) !== addressBaselineRef.current
    )
  }

  const { enableBillingTaxId } = useFeatureToggle()
  const usBilled = isUsBilling(billing)

  const { data, error } = useAxios({ url: 'billing/customers/billing-address/' })

  // Seed the address baseline from the saved billing address once it loads,
  // and record whether that saved address had no country — the signal that the
  // next AddressElement emit will be Stripe's auto-country hydration rather than
  // a user edit (see savedAddressWasEmptyRef + handleCustomerChange).
  useEffect(() => {
    if (data) {
      addressBaselineRef.current = billingAddressSnapshot(data)
      savedAddressWasEmptyRef.current = !data.address || !data.address.country
    }
  }, [data])

  const { data: taxIdData } = useAxios({
    url: 'billing/customers/tax-id/',
    skip: !enableBillingTaxId,
  })

  const { fetch: updateCustomer, loading } = useAxios({
    url: 'billing/customers/billing-address/',
    method: 'patch',
    skip: true,
  })

  const { fetch: updateTaxId } = useAxios({
    url: 'billing/customers/tax-id/',
    method: 'patch',
    skip: true,
  })

  // Seed taxIdValue once on first tax-id data arrival — value or null.
  // Guarding on any first arrival (not only non-null) prevents a later
  // background refetch from overwriting user input, and onTaxIdChange
  // flips the ref on first user interaction so a slow tax-id GET can't
  // clobber a value the user has already typed.
  const hasSeededTaxIdRef = useRef(false)

  useEffect(() => {
    if (!hasSeededTaxIdRef.current && taxIdData !== undefined && taxIdData !== null) {
      if (taxIdData.value) {
        setTaxIdValue(taxIdData.value)
      }
      hasSeededTaxIdRef.current = true
    }
  }, [taxIdData])

  const taxIdDirtyRef = useRef(taxIdDirty)
  taxIdDirtyRef.current = taxIdDirty
  const previousTaxIdDataRef = useRef(taxIdData)

  useEffect(() => {
    const previousTaxIdData = previousTaxIdDataRef.current
    previousTaxIdDataRef.current = taxIdData
    if (!previousTaxIdData || !taxIdData || taxIdDirtyRef.current) {
      return
    }
    if (previousTaxIdData.value !== taxIdData.value) {
      setTaxIdValue(taxIdData.value ?? '')
    }
  }, [taxIdData])

  // Country comes from local edits when present, otherwise from the initial GET —
  // so the tax-id field renders on first load for customers with an existing address.
  const country = customer?.address?.country ?? data?.address?.country
  const prevCountryRef = useRef(null)

  useEffect(() => {
    if (prevCountryRef.current !== null && prevCountryRef.current !== country) {
      setTaxIdValue('')
      // Also clear any partial-failure error tied to the previous country's
      // tax-id type — otherwise the field re-renders with a fresh label +
      // empty value but a stale error text underneath.
      setTaxIdInlineError(null)
      // NOTE: no backend PATCH here. The stored tax_id is deleted on Save
      // (via saveTaxId with an empty value, or via replace-then-delete when
      // the user enters a new value), NOT on country change. Firing an
      // immediate DELETE on country change dropped the tax_id from Stripe
      // even when the user was mid-edit and might change country back —
      // and when the user DID save, the incomplete new-country address
      // was silently dropped by the tax-id-only path, leaving a mismatched
      // (US address, JP TRN) state on refresh. Fix 2026-07-07.
    }
    if (country) {
      prevCountryRef.current = country
    }
  }, [country])

  const taxIdMeta = getTaxIdMetaForCountry(country)

  // Track whether tax-id has changed from what the GET returned, so Save
  // can unblock in the tax-id-only edit case (PO decision — Option A on
  // the Task Breakdown page's Tax-ID-only save section, 2026-07-07). The
  // ?? '' normalises the flag-off / no-stored-value shapes so an untouched
  // form never reads as "changed".
  const taxIdChanged = enableBillingTaxId && (taxIdData?.value ?? '') !== taxIdValue

  // Country-change flag — the third Save unblocker. Without this, a user
  // who changes the country but happens to re-enter the exact same tax-id
  // (or lands in the bug's mismatched-state residue) has NO way to click
  // Save, so the country-mismatch toast in onSubmit is never surfaced.
  //
  // Fresh accounts have no saved billing address at all — `data.address` is
  // null / `data.address.country` is undefined. Stripe's AddressElement then
  // auto-selects US in the dropdown, so US is the effective "as-saved" country
  // for change detection. Without the `|| 'US'` fallback, addressCountryChanged
  // was `'JP' && undefined && ...` = undefined (falsy) → button stuck disabled
  // no matter what country the user picked. Bug found 2026-07-07 on the E2E
  // Owner workspace which has no saved address.
  const currentCountry = customer?.address?.country
  const savedCountry = data?.address?.country || 'US'
  const addressCountryChanged = currentCountry && currentCountry !== savedCountry

  function onTaxIdChange(value) {
    hasSeededTaxIdRef.current = true
    setTaxIdValue(value)
    setTaxIdInlineError(null)
    setTaxIdDirty(true)
  }

  // 4xx → country-specific validation hint built from taxIdMeta (label +
  // placeholder). Stripe's raw error text ("Request req_...: Invalid value
  // for jp_trn.") is discarded — PO decision (Brian, 2026-07-07): show
  // "Please enter a valid {label} (e.g. {placeholder})." instead.
  // 5xx / network → generic retry copy (a Stripe / CF blip is not the
  // user's format error).
  function setTaxIdErrorFromException(taxIdError) {
    setTaxIdInlineError(getTaxIdErrorMessage(taxIdError, taxIdMeta))
  }

  function saveTaxId() {
    return updateTaxId({
      data: {
        value: taxIdValue,
        type: taxIdMeta?.type,
      },
    }).then((response) => {
      const savedTaxId = response?.data
      if (savedTaxId && 'value' in savedTaxId) {
        mutate('billing/customers/tax-id/', savedTaxId, false)
        setTaxIdValue(savedTaxId.value ?? '')
      }
      return response
    })
  }

  // The Save button unblocks when EITHER the address is complete OR the tax
  // ID has changed. `isAddressComplete` is passed up from the form's
  // Stripe AddressElement `event.complete` state.
  function onSubmit(isAddressComplete) {
    const willPatchTaxId = enableBillingTaxId && taxIdChanged
    const addressToSave = usBilled
      ? { name: customer.name ?? data.name, address: customer.address ?? data.address }
      : customer

    if (usBilled) {
      const missingFieldErrors = getMissingUsAddressErrors(addressToSave.address)
      if (missingFieldErrors.length > 0) {
        setAddressInlineError(missingFieldErrors.join('. '))
        return
      }
      if (!isAddressComplete) {
        setAddressInlineError('Please complete your billing address before saving.')
        return
      }
    }

    // Guard against the country-mismatch bug (2026-07-07): if the user
    // has changed the address country but hasn't completed the new
    // address, the tax-id-only path below would silently drop the
    // country change and persist ONLY the tax-id — leaving the customer
    // with a US-address + JP-TRN mismatch on refresh. Block with an
    // inline error under the AddressElement (matches the tax-id
    // inline-error pattern — user feedback 2026-07-07: no toast). The
    // Save button is unblocked in this same case (via
    // addressCountryChanged) so the user can click through to this
    // inline error instead of staring at a disabled button.
    if (addressCountryChanged && !isAddressComplete) {
      setAddressInlineError('Please complete your billing address before saving.')
      return
    }
    setAddressInlineError(null)

    // Tax-id-only path: address is incomplete but user edited the tax ID.
    // Skip the address PATCH entirely (would fail validation upstream anyway)
    // and just save the tax ID. PO decision (Brian, 2026-07-07): all success
    // paths — tax-id-only, address-only, address + tax-id — use the same
    // 'Billing details updated!' toast.
    if (!isAddressComplete && willPatchTaxId && !usBilled) {
      saveTaxId()
        .then(() => {
          toast.success('Billing details updated!')
          // Tax-id-only path — address was not touched, so don't re-baseline it.
          markSaved()
        })
        .catch(setTaxIdErrorFromException)
      return
    }

    // Address path (address-only OR address + tax-id) — same two-PATCH flow.
    // On partial success (address saves, tax-id 400s) we still show the
    // success toast: the address change DID persist, and the tax-id failure
    // surfaces via the inline error on the field (no separate 'Address saved'
    // toast — PO simplification 2026-07-07).
    updateCustomer({
      data: {
        name: addressToSave.name,
        address: addressToSave.address,
      },
    })
      .then(() => {
        mutate('billing/customers/billing-address/')
        if (willPatchTaxId) {
          return saveTaxId()
            .then(() => {
              toast.success('Billing details updated!')
              markSaved({ rebaseline: true })
            })
            .catch((taxIdError) => {
              toast.success('Billing details updated!')
              markSaved({ rebaseline: true })
              setTaxIdErrorFromException(taxIdError)
            })
        }
        toast.success('Billing details updated!')
        markSaved({ rebaseline: true })
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.detail ||
            'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
      })
  }

  const prompt = <UnsavedChangesGuard when={isDirty} />

  if (error) {
    return (
      <React.Fragment>
        <h3>Billing details</h3>
        <Message negative>
          <strong>Whoops!</strong> Something went wrong. Please contact Simplesat support to help
          you out.
        </Message>
      </React.Fragment>
    )
  } else if (!data) {
    return null
  } else {
    return (
      <BillingDetailForm
        companyName={companyName}
        onChange={handleCustomerChange}
        onSubmit={onSubmit}
        defaultCustomer={data}
        loading={loading}
        enableBillingTaxId={enableBillingTaxId}
        usBilled={usBilled}
        taxIdMeta={taxIdMeta}
        taxIdValue={taxIdValue}
        taxIdInlineError={taxIdInlineError}
        taxIdChanged={taxIdChanged}
        addressCountryChanged={addressCountryChanged}
        addressInlineError={addressInlineError}
        onTaxIdChange={onTaxIdChange}
        prompt={prompt}
      />
    )
  }
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/Billing/BillingDetailForm/container.js